import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import { useEffect, type FunctionComponent } from 'react'

export type DatadogProps = {
	applicationId: string,
	clientToken: string,
	service: string,
	env: string,
	version: string,
	isEnabled?: boolean,
}

let datadogInitialized = false

// Must be rendered inside BrowserRouter for reactPlugin({ router: true }) to work
export const Datadog: FunctionComponent<DatadogProps> = ( {
	applicationId,
	clientToken,
	service,
	env,
	version,
	isEnabled = true,
} ) => {
	useEffect( () => {
		if ( datadogInitialized || !isEnabled ) {
			return
		}
		datadogInitialized = true

		datadogRum.init( {
			applicationId,
			clientToken,
			site: 'datadoghq.com',
			service,
			env,
			version,
			sessionSampleRate: 100,
			sessionReplaySampleRate: 100,
			traceContextInjection: 'sampled',
			trackUserInteractions: true,
			trackResources: true,
			trackLongTasks: true,
			defaultPrivacyLevel: 'mask-user-input',
			plugins: [ reactPlugin( { router: true } ) ],
		} )

		datadogRum.startSessionReplayRecording()
	}, [ applicationId, clientToken, service, env, version, isEnabled ] )

	return null
}

export type DatadogRumViewTrackerProps = {
	isEnabled?: boolean,
}

export const DatadogRumViewTracker: FunctionComponent<DatadogRumViewTrackerProps> = ( { isEnabled = true } ) => {
	useEffect( () => {
		if ( !isEnabled || typeof window === 'undefined' ) {
			return
		}

		const reportView = () => {
			datadogRum.startView( { name: window.location.pathname } )
		}

		reportView()

		const { pushState, replaceState } = history
		history.pushState = function ( ...args: Parameters<History['pushState']> ) {
			pushState.apply( history, args )
			reportView()
		}
		history.replaceState = function ( ...args: Parameters<History['replaceState']> ) {
			replaceState.apply( history, args )
			reportView()
		}
		window.addEventListener( 'popstate', reportView )

		return () => {
			history.pushState = pushState
			history.replaceState = replaceState
			window.removeEventListener( 'popstate', reportView )
		}
	}, [ isEnabled ] )

	return null
}
